import {
  DataTableSource,
  type DataTablePageOptions,
  type DataTablePageReq,
  type DataTablePageResp,
  type DataTableSourceOptions,
} from "@carbon6/c6-frontend-design";
import {
  getTrainingGpuWorkspaceUsage,
  listTrainingGpuMemberQuotas,
  type TrainingGpuMemberQuota,
  type TrainingGpuWorkspaceUsage,
} from "@/api";
import { gpuUsageWindow, type GpuUsagePeriod } from "@/components/gpu/usage/gpuUsagePeriod";
import { DEFAULT_GPU_QUOTA_USAGE_PERIOD } from "./gpuQuotaFilters";
import {
  createGpuQuotaTableRows,
  GpuQuotaMemberRow,
  memberQuotaRowId,
  type GpuQuotaTableRow,
} from "./GpuQuotaTableRow";
import { resolveGpuQuotaErrorMessage } from "./gpuQuotaFormat";

// 사람별 쿼터와 같은 기간 사용량을 한 표로 합치는 소스. 두 응답 모두 페이지네이션이 없다
// (사람·workspace 수는 적다). 그래서 요청 page/size 는 무시하고 한 페이지로 만든다.
//
// 펼침 상태와 검색어는 이 소스가 소유한다. 내역을 열고 닫을 때와 검색어가 바뀔 때는 서버를 다시
// 호출하지 않는다. 응답 원본을 보관하고 행만 다시 조립해 커밋한다. 기간이 바뀌면 사용량이
// 달라지므로 다시 조회한다.
export class GpuQuotaTableSource extends DataTableSource<GpuQuotaTableRow> {
  private period: GpuUsagePeriod;
  private query: string;
  private memberQuotas: readonly TrainingGpuMemberQuota[] = [];
  // 직전 조회의 사용량 응답. 첫 조회가 끝나기 전에는 null 이다.
  private usage: TrainingGpuWorkspaceUsage | null = null;
  private expandedRowIds: ReadonlySet<string> = new Set();
  // 지금 표에 보이는 선점자 수. 미할당 행과 꼬리 행은 사람이 아니므로 세지 않는다.
  private memberCount = 0;
  // 지금 표에 보이는 최상위 행 중 가장 큰 기간 GPU-hours. 사용량 막대의 분모다.
  private maxGpuHours = 0;

  constructor({
    // URL 에서 읽은 첫 조건. 첫 로드에 바로 적용한다. 그래서 initialize 뒤 setter 는 no-op 이 된다.
    initialPeriod = DEFAULT_GPU_QUOTA_USAGE_PERIOD,
    initialQuery = "",
    ...options
  }: DataTableSourceOptions & {
    initialPeriod?: GpuUsagePeriod;
    initialQuery?: string;
  } = {}) {
    // c6 기본 실패 문구는 영어이므로 로케일 문구를 여기서 주입한다. 호출부가 덮어쓸 수 있도록
    // options 를 뒤에 펼친다.
    super({ resolveErrorMessage: resolveGpuQuotaErrorMessage, ...options });
    this.period = initialPeriod;
    this.query = initialQuery;
  }

  // 검색을 통과한 선점자 수. 결과 수 문구가 이 값을 쓴다.
  getMemberCount(): number {
    return this.memberCount;
  }

  // 사용량 막대의 분모. 하위 행은 사람 행의 일부이므로 이 값을 넘지 않는다.
  getMaxGpuHours(): number {
    return this.maxGpuHours;
  }

  // 직전 조회의 사용량 응답. 요약 타일이 합계와 분해를 여기서 읽는다.
  getUsage(): TrainingGpuWorkspaceUsage | null {
    return this.usage;
  }

  setPeriod(period: GpuUsagePeriod): Promise<void> {
    if (period === this.period) {
      return Promise.resolve();
    }
    this.period = period;
    return this.refresh();
  }

  setQuery(query: string): void {
    if (query === this.query) {
      return;
    }
    this.query = query;
    this.recommitRows();
  }

  // 사람 행 안의 내역을 펼치거나 접는다. 이미 받은 응답만 다시 조립하므로 서버 왕복이 없다.
  toggleMember(rowId: string): void {
    const next = new Set(this.expandedRowIds);
    if (!next.delete(rowId)) {
      next.add(rowId);
    }
    this.expandedRowIds = next;
    this.recommitRows();
  }

  protected override async onPage(
    req: DataTablePageReq,
    { signal }: DataTablePageOptions,
  ): Promise<DataTablePageResp<GpuQuotaTableRow>> {
    // 두 요청은 서로 독립이므로 동시에 보낸다. 한쪽이 실패하면 표 전체를 에러로 표시한다. 한도만
    // 있거나 사용량만 있는 표는 요청 판단의 근거가 절반뿐이므로 부분 성공으로 그리지 않는다.
    const [quotas, usage] = await Promise.all([
      listTrainingGpuMemberQuotas({ signal }),
      getTrainingGpuWorkspaceUsage(gpuUsageWindow(this.period, Date.now()), { signal }),
    ]);

    this.memberQuotas = quotas;
    this.usage = usage;
    this.expandedRowIds = retainExistingRows(this.expandedRowIds, quotas);

    const rows = this.buildRows();
    return { rows, total: rows.length, page: 1, size: Math.max(rows.length, req.size) };
  }

  // 보관한 응답으로 행을 다시 조립해 커밋한다. 첫 조회 전에는 조립할 응답이 없다.
  private recommitRows(): void {
    if (this.usage === null) {
      return;
    }
    const rows = this.buildRows();
    this.replaceLoadedPage({
      rows,
      total: rows.length,
      page: 1,
      size: Math.max(rows.length, this.getSnapshot().size),
    });
  }

  private buildRows(): GpuQuotaTableRow[] {
    if (this.usage === null) {
      return [];
    }
    // 행 클릭(사람 행 전체)과 캐럿 버튼이 같은 토글을 호출한다.
    const rows = createGpuQuotaTableRows(
      this.memberQuotas,
      this.usage,
      this.expandedRowIds,
      (row) => this.toggleMember(row.getRowId()),
    ).filter((row) => row.matchesQuery(this.query));
    this.memberCount = rows.filter(
      (row) => row instanceof GpuQuotaMemberRow && row.member !== null,
    ).length;
    this.maxGpuHours = rows.reduce((highest, row) => Math.max(highest, row.gpuHours), 0);
    return rows;
  }
}

// 새로 읽은 응답에 없는 사람의 펼침 상태는 버린다. 남겨 두면, 선점이 다른 사람에게 옮겨 간 뒤
// 다시 돌아온 사람이 예고 없이 펼쳐진 상태로 나타난다.
function retainExistingRows(
  expandedRowIds: ReadonlySet<string>,
  quotas: readonly TrainingGpuMemberQuota[],
): ReadonlySet<string> {
  const alive = new Set(quotas.map((quota) => memberQuotaRowId(quota.member)));
  return new Set([...expandedRowIds].filter((rowId) => alive.has(rowId)));
}
